import React, { useState } from 'react'
import { getValidPreviousIntentId } from '../../../../utils/functions/getValidPreviousIntentId'
import { updateWhatsAppIntent } from '../../../../utils/functions/updateWhatsAppIntent'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import { uniqueId } from 'lodash'
import TriggerDescription from '../TriggerDescription'

interface IProps {
  intent: any
  inputJson: any[]
  setInputJson: React.Dispatch<React.SetStateAction<any[]>>
  closeModal: () => void
  data: any
}

const Action: React.FC<IProps> = ({ intent, inputJson, setInputJson, closeModal, data }) => {
  const intentIndex = inputJson.findIndex((item) => item.id === intent.id)
  const [isSaving, setIsSaving] = useState(false)

  const [method, setMethod] = useState(intent?.method || 'GET')
  const [url, setUrl] = useState(intent?.url || '')
  const [body, setBody] = useState<Record<string, string>>(intent?.body || { name: '', phone: '' })
  const [msgRes, setMsgRes] = useState<Record<string, number>>(intent?.msgRes || { default: 0 })

  const STATUS_CODES = [
    { code: 'default', label: 'default' },
    { code: '100', label: '100 Continue' },
    { code: '101', label: '101 Switching Protocols' },
    { code: '200', label: '200 OK' },
    { code: '201', label: '201 Created' },
    { code: '202', label: '202 Accepted' },
    { code: '204', label: '204 No Content' },
    { code: '301', label: '301 Moved Permanently' },
    { code: '302', label: '302 Found' },
    { code: '304', label: '304 Not Modified' },
    { code: '400', label: '400 Bad Request' },
    { code: '401', label: '401 Unauthorized' },
    { code: '403', label: '403 Forbidden' },
    { code: '404', label: '404 Not Found' },
    { code: '409', label: '409 Conflict' },
    { code: '422', label: '422 Unprocessable Entity' },
    { code: '429', label: '429 Too Many Requests' },
    { code: '500', label: '500 Internal Server Error' },
    { code: '502', label: '502 Bad Gateway' },
    { code: '503', label: '503 Service Unavailable' },
    { code: '504', label: '504 Gateway Timeout' }
  ]

  const handleBodyChange = (key: string, value: string) => {
    setBody({ ...body, [key]: value })
  }

  const handleAddBodyField = () => {
    setBody({ ...body, [`key${Object.keys(body).length + 1}`]: '' })
  }

  const handleMsgResChange = (status: string, value: number) => {
    setMsgRes({ ...msgRes, [status]: value })
  }

  const handleAddMsgRes = () => {
    setMsgRes({ ...msgRes, [`${Object.keys(msgRes).length * 100}`]: 0 })
  }

  const handleSave = async () => {
    const token = localStorage.getItem('auth-token')
    const whatsAppIDString = data?.whatsAppIDString
    const previousIntentId = getValidPreviousIntentId(
      intentIndex,
      inputJson,
      data?.WhatsAppInput || []
    )
    setIsSaving(true)

    const newIntent = {
      ...intent,
      method,
      url,
      body,
      msgRes
    }

    const newInputJson = [...inputJson]
    newInputJson[intentIndex] = newIntent
    setInputJson(newInputJson)

    await updateWhatsAppIntent({
      token,
      whatsAppIDString,
      intent,
      intentJson: newIntent,
      previousIntentId,
      onSuccess: closeModal
    })
  }

  return (
    <div>
      <InputGroup className='my-3'>
        <InputGroup.Text>Method</InputGroup.Text>
        <Form.Select value={method} onChange={(e) => setMethod(e.target.value)}>
          <option value='GET'>GET</option>
          <option value='POST'>POST</option>
        </Form.Select>
      </InputGroup>

      <InputGroup className='my-3'>
        <InputGroup.Text>URL</InputGroup.Text>
        <Form.Control
          type='text'
          placeholder='https://api.example.com/endpoint'
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </InputGroup>

      <h6>Body</h6>
      {Object.entries(body).map(([key, value]) => {
        const storedTags: string[] = JSON.parse(sessionStorage.getItem('usedTags') || '[]')
        const selectedTags = Object.values(body)

        return (
          <InputGroup className='my-2' key={uniqueId()}>
            {/* Key input */}
            {/* Key input */}
            <Form.Control
              placeholder='Key'
              defaultValue={key} // use defaultValue to avoid constant re-renders
              onBlur={(e) => {
                const newKey = e.target.value.trim()
                if (!newKey || newKey === key) return // no change

                // Rename key safely
                const { [key]: _, ...rest } = body
                setBody({ ...rest, [newKey]: value })
              }}
            />

            {/* Value dropdown (strictly tags only) */}
            <Form.Select value={value} onChange={(e) => handleBodyChange(key, e.target.value)}>
              <option value=''>-- Select a tag --</option>
              {storedTags
                .filter((tag) => !selectedTags.includes(tag) || tag === value) // prevent duplicates, allow current
                .map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
            </Form.Select>

            {/* Delete button */}
            <button
              className='btn btn-danger'
              type='button'
              onClick={() => {
                const { [key]: _, ...rest } = body
                setBody(rest)
              }}
            >
              <i className='bi bi-trash' />
            </button>
          </InputGroup>
        )
      })}

      {/* Add Body Button */}
      <div className='d-flex justify-content-start mt-2'>
        <button className='btn btn-sm btn-primary' type='button' onClick={handleAddBodyField}>
          + Add Body Field
        </button>
      </div>

      {/* ---- MsgRes Fields ---- */}
      <h6 className='mt-4'>Message Responses</h6>
      {Object.entries(msgRes).map(([status, intentId], idx) => (
        <InputGroup className='my-2' key={uniqueId()}>
          {/* Status Code Dropdown */}
          <Form.Select
            value={status}
            onChange={(e) => {
              const newKey = e.target.value
              const { [status]: _, ...rest } = msgRes
              setMsgRes({ ...rest, [newKey]: intentId })
            }}
            disabled={status === 'default'} // default can't be changed
          >
            {STATUS_CODES.map((s) => (
              <option key={s.code} value={s.code} disabled={s.code === 'default'}>
                {s.label}
              </option>
            ))}
          </Form.Select>

          {/* Intent ID Dropdown (using TriggerDescription) */}
          <Form.Select
            value={intentId}
            onChange={(e) => handleMsgResChange(status, Number(e.target.value))}
          >
            <TriggerDescription inputJson={inputJson} />
          </Form.Select>

          {/* Delete Button (disabled for default) */}
          {/* {status !== 'default' && ( */}
          <button
            className='btn btn-danger'
            type='button'
            disabled={status === 'default'}
            onClick={() => {
              const { [status]: _, ...rest } = msgRes
              setMsgRes(rest)
            }}
          >
            <i className='bi bi-trash' />
          </button>
          {/* )} */}
        </InputGroup>
      ))}
      <button className='btn btn-sm btn-primary mt-2' onClick={handleAddMsgRes}>
        + Add MsgRes
      </button>

      {/* ---- Buttons ---- */}
      <div className='d-flex justify-content-end'>
        <button className='btn btn-secondary me-4' onClick={closeModal}>
          <i className='bi bi-eye-fill' /> Preview
        </button>
        <button className='btn btn-primary' onClick={handleSave} disabled={isSaving}>
          <i className='bi bi-save' /> {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}

export default Action
